import App from 'antd/lib/app';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ICreateSavedFilter, ISavedFilter, SavedFilterSubType } from '../../../components/sidebar-filters/saved-filter/interface';
import { useSavedFiltersList } from '../../../components/sidebar-filters/useSavedFiltersList';
import { DEFAULT_PAGINATION } from '../../../domain/commonConst';
import { useSavedFilterCreateMutation, useSavedFilterDeleteMutation, useSavedFilterUpdateMutation } from '../../../domain/saved-filters/queries';
import { useSidebarFilterOptionsContext } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { usePageLocation } from '../../usePageState';
import {
    checkFilterInvalidValues,
    convertReadinessTrackingPageStateToFilterState,
    countNotEmptyFilters,
    transformReadinessTrackingFilterValuesFromDto,
    transformReadinessTrackingFilterValuesToDto,
} from './utils';

export interface IReadinessTrackingPageState {
    filter?: {
        programId?: number | number[];
        userId?: number | number[];
        status?: string | string[];
        dateToSendEmail?: string;
        emailOpened?: boolean;
    };

    // Used by whole page
    companyId?: number;
    search?: string;

    pagination?: {
        current: number;
        pageSize: number;
    };

    sorter?: {
        field: string;
        order: 'ascend' | 'descend';
    };
}

export interface IReadinessTrackingSidebarFilters {
    programId?: number[];
    userId?: number[];
    status?: string[];

    dateToSendEmail?: string;
    emailOpened?: boolean;
}

export interface IReadinessTrackingSidebarFiltersContext {
    filtersCount: number;
    filters: IReadinessTrackingSidebarFilters;
    savedFilters: ISavedFilter[];

    isSavedFiltersLoading: boolean;

    handleFilterChange: (key: string, value: any) => void;
    handleFiltersClear: () => void;

    selectedSavedFilter: ICreateSavedFilter | null;
    isCreateFilterLoading: boolean;
    isUpdateFilterLoading: boolean;
    isDeleteFilterLoading: boolean;

    handleSavedFilterSelect: (filter: ICreateSavedFilter | null) => void;
    handleCreateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleUpdateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleDeleteSavedFilter: (filter: ISavedFilter) => Promise<void>;

    appliedSavedFilter: ISavedFilter | null; // Using for applying filters
    handleApplySavedFilter: (savedFilter: ISavedFilter) => void;
}

export const ReadinessTrackingSidebarFiltersContext = React.createContext<IReadinessTrackingSidebarFiltersContext>(
    {} as IReadinessTrackingSidebarFiltersContext
);

const useSidebarFiltering = () => {
    const location = useLocation();
    const { queryToState, setPageQuery } = usePageLocation();
    const pageState: IReadinessTrackingPageState = queryToState(location.search);

    const [filters, setFilters] = useState<IReadinessTrackingSidebarFilters>(convertReadinessTrackingPageStateToFilterState(pageState));
    const filtersCount = useMemo(() => countNotEmptyFilters(filters), [filters]);

    const handleFilterChange = (key: string, value: unknown) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
        }));

        // Update the URL query parameters with filter object structure
        const updatedPageState = {
            ...pageState,
            filter: {
                ...pageState.filter,
                [key]: value,
            },
            pagination: {
                ...pageState.pagination,
                current: DEFAULT_PAGINATION.current,
            },
        };

        setPageQuery(updatedPageState);
    };

    const handleFiltersChange = (newFilters: IReadinessTrackingSidebarFilters) => {
        setFilters(newFilters);
        setPageQuery({
            ...pageState,
            filter: newFilters,
            pagination: {
                ...pageState.pagination,
                current: DEFAULT_PAGINATION.current,
            },
        });
    };

    const handleFiltersClear = () => {
        setFilters({});

        setPageQuery({
            ...pageState,
            filter: {},
        });
    };

    return {
        filtersCount,
        filters,

        handleFilterChange,
        handleFiltersChange,
        handleFiltersClear,
    };
};

export const ReadinessTrackingSidebarFiltersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { filtersCount, filters, handleFilterChange, handleFiltersChange, handleFiltersClear } = useSidebarFiltering();

    // Saving Filters
    const {
        savedFilters,
        isSavedFiltersLoading,
        selectedSavedFilter,
        appliedSavedFilter,
        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,
        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
    } = useReadinessTrackingSavedFilters();

    const onFiltersChange = useCallback(
        (key: string, value: unknown) => {
            handleFilterChange(key, value);
            handleApplySavedFilter(null); // Reset applied filter when filters change
        },
        [handleApplySavedFilter, handleFilterChange]
    );
    // Combine logic for applying saved filters
    const onApplySavedFilter = useCallback(
        (savedFilter: ISavedFilter) => {
            const filterValues = savedFilter.filter_values;

            handleFiltersChange(filterValues);
            handleApplySavedFilter(savedFilter);
        },
        [handleApplySavedFilter, handleFiltersChange]
    );

    const onFiltersClear = useCallback(() => {
        handleFiltersClear();
        handleApplySavedFilter(null);
    }, [handleApplySavedFilter, handleFiltersClear]);

    return (
        <ReadinessTrackingSidebarFiltersContext.Provider
            value={{
                filtersCount,
                filters,
                savedFilters,

                isSavedFiltersLoading,

                handleFilterChange: onFiltersChange,
                handleFiltersClear: onFiltersClear,

                selectedSavedFilter,
                isCreateFilterLoading,
                isUpdateFilterLoading,
                isDeleteFilterLoading,
                handleSavedFilterSelect,
                handleCreateSavedFilter,
                handleUpdateSavedFilter,
                handleDeleteSavedFilter,

                appliedSavedFilter,
                handleApplySavedFilter: onApplySavedFilter,
            }}
        >
            {children}
        </ReadinessTrackingSidebarFiltersContext.Provider>
    );
};

export const useReadinessTrackingSidebarFiltersContext = () => {
    const context = React.useContext(ReadinessTrackingSidebarFiltersContext);
    if (!context) {
        throw new Error('useReadinessTrackingSidebarFiltersContext must be used within ReadinessTrackingSidebarFiltersContext');
    }

    return context;
};

const useReadinessTrackingSavedFilters = () => {
    const { notification } = App.useApp();
    const context = useSidebarFilterOptionsContext();

    // This filter state using to track applied saved filter
    const [appliedSavedFilter, setAppliedSavedFilter] = useState<ISavedFilter | null>(null);

    // This filter state using to create/update saved filter
    const [selectedSavedFilter, setSelectedSavedFilter] = useState<ICreateSavedFilter | null>(null);
    const { savedFilters, isLoading: isSavedFiltersLoading } = useSavedFiltersList(SavedFilterSubType.readinessTracking);

    const { mutateAsync: createSavedFilter, isLoading: isCreateFilterLoading } = useSavedFilterCreateMutation();
    const { mutateAsync: deleteSavedFilter, isLoading: isDeleteFilterLoading } = useSavedFilterDeleteMutation();
    const { mutateAsync: updateSavedFilter, isLoading: isUpdateFilterLoading } = useSavedFilterUpdateMutation();

    const validateSavedFilter = useCallback(
        (filter: ISavedFilter) => {
            // Transform filter values from DTO format to Interface
            filter.filter_values = transformReadinessTrackingFilterValuesFromDto(filter.filter_values);

            const outdatedFields = checkFilterInvalidValues(filter.filter_values, context);
            // Check if the filter has not existing values in options
            filter.isOutdated = outdatedFields.length > 0;
            filter.outdatedFields = outdatedFields;

            return filter;
        },
        [context]
    );

    const validatedSavedFilters = useMemo(() => {
        if (savedFilters.length === 0) return [];

        return savedFilters.map((filter: any) => validateSavedFilter(filter));
    }, [savedFilters, validateSavedFilter]);

    const handleSavedFilterSelect = (filter: ICreateSavedFilter | null) => {
        setSelectedSavedFilter(filter);
    };

    const handleApplySavedFilter = (savedFilter: ISavedFilter | null) => {
        setAppliedSavedFilter(savedFilter);
    };

    const handleCreateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            // Transform filter values to DTO format
            filterData.filter_values = transformReadinessTrackingFilterValuesToDto(filterData.filter_values);

            await createSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot save filter!' });
        }
    };

    const handleUpdateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            // Transform filter values to DTO format
            filterData.filter_values = transformReadinessTrackingFilterValuesToDto(filterData.filter_values);

            await updateSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot update filter!' });
        }
    };

    const handleDeleteSavedFilter = async (filter: ISavedFilter) => {
        try {
            await deleteSavedFilter(filter.id);

            if (selectedSavedFilter?.id === filter.id) {
                setSelectedSavedFilter(null);
            }

            if (appliedSavedFilter?.id === filter.id) {
                setAppliedSavedFilter(null);
            }
        } catch (err) {
            notification.error({ message: 'Cannot delete filter!' });
        }
    };

    return {
        savedFilters: validatedSavedFilters,
        selectedSavedFilter,
        appliedSavedFilter,
        isSavedFiltersLoading,
        validateSavedFilter,

        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,

        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
    };
};
